import { useCallback, useState } from 'react';

/**
 * A custom hook to manage a boolean state with helper methods.
 *
 * @param {boolean} [initialValue=false] - The initial value of the boolean state.
 *
 * @returns {[boolean, {
 *   toggle: () => void,
 *   setToTrue: () => void,
 *   setToFalse: () => void
 * }]} A tuple containing:
 *   - value: The current boolean state.
 *   - controls: An object with:
 *     - `toggle()`: Inverts the current value.
 *     - `setToTrue()`: Sets the value to true.
 *     - `setToFalse()`: Sets the value to false.
 *
 * @example
 * const [isOpen, { toggle, setToTrue, setToFalse }] = useBoolean(false);
 *
 * toggle();     // flips the state
 * setToTrue();  // sets to true
 * setToFalse(); // sets to false
 */
export function useBoolean(initialValue) {
	const [value, setValue] = useState(initialValue ?? false);

	const setToFalse = useCallback(() => setValue(false), []);
	const setToTrue = useCallback(() => setValue(true), []);
	const toggle = useCallback(() => setValue((value) => !value), []);

	return [value, { toggle, setToFalse, setToTrue }];
}
